import { ChangeWorldIpSchema } from './world-network.js';
import { MoveNodeParamsSchema } from './nodes.js';
import { JvmSettingsSchema } from './jvm.js';
import { RegenerateWorldSchema } from './server-settings.js';
import { WorldGenerationSchema } from './world-settings.js';
import { GameSettingsPatchSchema } from './world-settings.js';
import { ChangePortsParamsSchema } from './ports.js';
import { z } from 'zod';
import { ChangeModpackSchema } from './modpack.js';
import { BedrockSettingsSchema } from './bedrock.js';
import { CreateServerSchema, SoftwareSpecSchema, ViaSchema } from './domain.js';
import { ConsoleInputSchema } from './web.js';
import { ModChangeSchema } from './mods.js';
import { ChangeResourcesParamsSchema } from './maintenance.js';
import { CreateServerRequestSchema } from './templates.js';
/** Proxmox のスナップショット名の規則（pve-configid・最大 40 文字）に合わせる */
export const SNAPSHOT_NAME_MAX = 40;
export const SnapshotNameSchema = z.string().regex(/^[a-zA-Z][a-zA-Z0-9_-]{0,39}$/);
/**
 * スナップショットの名前を作る。種類を短い語にし、Proxmox の 40 文字の上限に必ず収める。
 * 例: snapshotName('sw', '202610111316', 'a2e1184b') → 'pre-sw-202610111316-a2e1184b'（28 文字）
 */
export function snapshotName(kind: string, stamp: string, suffix: string, prefix = 'pre'): string {
  const name = [prefix, kind, stamp, suffix]
    .filter(Boolean)
    .join('-')
    .replace(/[^a-zA-Z0-9_-]/g, '_');
  return SnapshotNameSchema.parse(name.slice(0, SNAPSHOT_NAME_MAX));
}
export const RequestKindSchema = z.enum([
  'create_server',
  'delete_server',
  'change_software',
  'change_version',
  'change_mods',
  'change_modpack',
  'change_bedrock',
  'change_ports',
  'change_world_generation',
  'regenerate_world',
  'change_game_settings',
  'change_jvm_args',
  'restore_snapshot',
  'delete_snapshot',
  'console_command',
  'apply_edge',
  'change_resources',
  'move_node',
  'change_world_ip',
]);
/**
 * 取り返しのつかない変更。5 分間の再確認を使い回さず、依頼を作ったあとに入れたワンタイムコードでだけ承認できる。
 * （2026-10-11、別の変更で入れたコードが、2 分後の削除の承認に使い回された事故を受けて追加）
 */
export const freshCodeRequestKinds: readonly string[] = ['delete_server', 'restore_snapshot'];
/**
 * 元に戻しやすい変更。ワンタイムコードを求めない（承認は今までどおり Web のセッションでだけ行う）。
 * ワールドの再生成は頻繁に行うため（2026-10-10）、ほかは設定の調整で毎回聞かれるのが煩わしいため（2026-10-12）。
 */
export const noCodeRequestKinds: readonly string[] = [
  'change_game_settings',
  'change_jvm_args',
  'change_resources',
  'change_world_generation',
  'regenerate_world',
];
export const RequestStatusSchema = z.enum(['pending', 'approved', 'rejected', 'expired', 'executed', 'failed']);
export const RequestInputSchema = z
  .object({
    kind: RequestKindSchema.exclude(['apply_edge']),
    serverId: z.string().optional(),
    params: z.record(z.string(), z.unknown()),
  })
  .strict();
export const SoftwareWorldSchema = z.object({ mode: z.literal('new') }).strict();
export const SoftwareChangeParamsSchema = z
  .object({ software: SoftwareSpecSchema, world: SoftwareWorldSchema.optional() })
  .strict();

export const RequestParamsSchemas = {
  create_server: CreateServerRequestSchema,
  delete_server: z.object({}).strict(),
  change_software: SoftwareChangeParamsSchema,
  change_version: SoftwareChangeParamsSchema,
  change_mods: ModChangeSchema,
  change_modpack: ChangeModpackSchema,
  change_bedrock: BedrockSettingsSchema,
  change_ports: ChangePortsParamsSchema,
  change_world_generation: WorldGenerationSchema,
  regenerate_world: RegenerateWorldSchema,
  change_game_settings: GameSettingsPatchSchema,
  change_jvm_args: JvmSettingsSchema,
  restore_snapshot: z.object({ snapshot: SnapshotNameSchema }).strict(),
  delete_snapshot: z.object({ snapshot: SnapshotNameSchema }).strict(),
  console_command: ConsoleInputSchema,
  apply_edge: z.object({}).strict(),
  change_resources: ChangeResourcesParamsSchema,
  move_node: MoveNodeParamsSchema,
  change_world_ip: ChangeWorldIpSchema,
};
export const ChangeRequestSchema = RequestInputSchema.extend({
  // 過去の edge 反映依頼を履歴として読むためだけに保持する。新しい依頼には使えない。
  kind: RequestKindSchema,
  id: z.string(),
  serverRevision: z.number().int().optional(),
  plan: z.object({
    summary: z.string(),
    changes: z.array(z.object({ label: z.string(), before: z.string().optional(), after: z.string().optional() })),
    steps: z.array(z.string()).optional(),
    estimatedMinutes: z.number().optional(),
  }),
  warnings: z.array(z.object({ level: z.enum(['warn', 'danger']), message: z.string() })),
  status: RequestStatusSchema,
  requestedBy: z.string(),
  via: ViaSchema,
  createdAt: z.string(),
  expiresAt: z.string(),
  decidedBy: z.string().optional(),
  decidedAt: z.string().optional(),
  jobId: z.string().optional(),
  autoExecuted: z.boolean().optional(),
  /** 計画時に確定した実行内容（承認されたものをそのまま実行するため）。API の応答には含めない */
  payload: z.record(z.string(), z.unknown()).optional(),
});
export const SnapshotSchema = z.object({
  name: SnapshotNameSchema,
  createdAt: z.string(),
  note: z.string().optional(),
  kind: z.enum(['manual', 'auto']),
  software: SoftwareSpecSchema.optional(),
  createdBy: z.string().optional(),
});
export const SnapshotInputSchema = z.object({ note: z.string().max(1000).optional() }).strict();
export const RequestsQuerySchema = z
  .object({ status: z.enum(['pending', 'all']).default('all'), serverId: z.string().optional() })
  .strict();
export const VersionsQuerySchema = z.object({ snapshots: z.enum(['true', 'false']).default('false') }).strict();
export const SoftwareBuildParamsSchema = z
  .object({ family: SoftwareSpecSchema.shape.family, mcVersion: z.string().min(1).max(100) })
  .strict();
export const SoftwareInfoSchema = z.object({
  family: SoftwareSpecSchema.shape.family,
  label: z.string(),
  layoutFamily: z.enum(['vanilla', 'bukkit']),
  kind: z.enum(['plugin', 'mod', 'vanilla']),
  supported: z.boolean(),
  proxyable: z.boolean(),
});
export const VersionInfoSchema = z.object({
  id: z.string(),
  type: z.enum(['release', 'snapshot']),
  releaseTime: z.string(),
  availability: z.enum(['available', 'unchecked']).optional(),
  message: z.string().optional(),
});
export const BuildInfoSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  recommended: z.boolean().optional(),
});
export type ChangeRequest = z.infer<typeof ChangeRequestSchema>;
export type RequestKind = z.infer<typeof RequestKindSchema>;
export type Snapshot = z.infer<typeof SnapshotSchema>;
export type RequestInput = z.infer<typeof RequestInputSchema>;
export type RequestStatus = z.infer<typeof RequestStatusSchema>;
export type PlanChange = ChangeRequest['plan']['changes'][number];
export type RequestWarning = ChangeRequest['warnings'][number];
export type SoftwareInfo = z.infer<typeof SoftwareInfoSchema>;
export type VersionInfo = z.infer<typeof VersionInfoSchema>;
export type BuildInfo = z.infer<typeof BuildInfoSchema>;

export const DowngradeOptionsSchema = z.object({ downgrade: z.boolean(), snapshots: SnapshotSchema.array() });
export type DowngradeOptions = z.infer<typeof DowngradeOptionsSchema>;
